import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Card,
  Row,
  Col,
  Avatar,
  Typography,
  Tabs,
  Spin,
} from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { fetchDoctorDetails } from "../../Redux/Slice/Patient/Doctors";
import dayjs from "dayjs";
import { createBooking } from "../../Redux/Slice/Patient/Booking";
import { toast } from "react-toastify";
import { fetchDoctorAvailability } from "../../Redux/Slice/Doctor/Slots";
// import { Radio } from "antd";

const { Option } = Select;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const DoctorBooking = () => {
  const dispatch = useDispatch();
  const [bookModalVisible, setBookModalVisible] = useState(false);
  const [form] = Form.useForm();

  // Filters
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedSpeciality, setSelectedSpeciality] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs()); // default today
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [interval] = useState(15);
  const [scheduleType, setScheduleType] = useState("morning");

  const user = JSON.parse(localStorage.getItem("user"));
  const patientId = user?.patientId;

  const { data: doctorData, loading: doctorLoading } = useSelector(
    (state) => state.doctorDetail.doctors
  );

  const {
    data: bookingData,
    loading: bookingLoading,
    error: bookingError,
  } = useSelector((state) => state.booking.bookingData);

  const { data: availableSlot } = useSelector(
    (state) => state.slot.availableSlot
  );

  useEffect(() => {
    if (selectedDoctor?.doctorId && selectedDate) {
      dispatch(
        fetchDoctorAvailability({
          interval,
          selectedDate: dayjs(selectedDate).format("YYYY-MM-DD"),
          doctorId: selectedDoctor.doctorId,
        })
      );
    }
  }, [dispatch, selectedDoctor, selectedDate, interval]);

  useEffect(() => {
    if (bookingData?.statusCode === 200) {
      toast.success(bookingData.message || "Booking successfully!");
      setBookModalVisible(false);
      form.resetFields();
    } else if (bookingError) {
      toast.error(
        bookingError?.message || "Failed to book appointment. Please try again."
      );
    }
  }, [bookingData, bookingError, form]);

  // ✅ Call API whenever branch, speciality, or date changes
  useEffect(() => {
    if (selectedBranch || selectedSpeciality || selectedDate) {
      const payload = {
        branchId: selectedBranch,
        specialityId: selectedSpeciality,
        date: selectedDate
          ? dayjs(selectedDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
      };

      dispatch(fetchDoctorDetails(payload));
    }
  }, [dispatch, selectedBranch, selectedSpeciality, selectedDate]);

  // Open Modal
  const openBooking = (doctorObj = null) => {
    setBookModalVisible(true);
    setSelectedDoctor(doctorObj);
    if (doctorObj) {
      form.setFieldsValue({
        doctorId: doctorObj.doctorId, // ✅ use doctorId
        branchId: doctorObj.mergedBranches[0]?.branchId,
        specialityId: doctorObj.mergedSpecialities[0]?.specialityId,
      });
    }
  };

  // Handle booking
  const handleBook = (values) => {
    const payload = {
      patientId,
      doctorId: values.doctorId,
      branchId: values.branchId,
      specialityId: values.specialityId,
      appointmentDate: dayjs(values.appointmentDate).format("YYYY-MM-DD"), // ✅ date format
      appointTime: values.appointTime, // already in "h:mm A" format
      appointmentType: values.appointmentType || "General", // default
      timeInterval: interval, // from state
      callType: values.callType || "Video", // default
      notes: values.notes || "",
      scheduleType:
        scheduleType.charAt(0).toUpperCase() + scheduleType.slice(1),
    };

    dispatch(createBooking(payload));
  };

  // Merge doctors with multiple branches/specialities
  const mergedDoctors = Object.values(
    doctorData?.data?.reduce((acc, curr) => {
      const docId = curr.doctor.doctorId;
      if (!acc[docId]) {
        acc[docId] = {
          ...curr.doctor,
          mergedBranches: [],
          mergedSpecialities: [],
          schedule: curr.schedule,
        };
      }
      if (
        !acc[docId].mergedBranches.find(
          (b) => b.branchId === curr.branch.branchId
        )
      ) {
        acc[docId].mergedBranches.push(curr.branch);
      }
      if (
        !acc[docId].mergedSpecialities.find(
          (s) => s.specialityId === curr.speciality.specialityId
        )
      ) {
        acc[docId].mergedSpecialities.push(curr.speciality);
      }
      return acc;
    }, {}) || {}
  );

  // Filter doctors
  const filteredDoctors = mergedDoctors.filter((doc) => {
    let matchesBranch = selectedBranch
      ? doc.mergedBranches.some((b) => b.branchId === selectedBranch)
      : true;
    let matchesSpeciality = selectedSpeciality
      ? doc.mergedSpecialities.some(
          (s) => s.specialityId === selectedSpeciality
        )
      : true;
    return matchesBranch && matchesSpeciality;
  });

  return (
    <div style={{ padding: 20 }}>
      <Card
        style={{ borderRadius: 16, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
        title={<Title level={3}>Book Appointment</Title>}
        extra={
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Select
              placeholder="Filter by Branch"
              style={{ width: 180 }}
              allowClear
              onChange={(val) => setSelectedBranch(val)}
            >
              {[
                ...new Set(doctorData?.data?.map((d) => d.branch.branchId)),
              ].map((id) => {
                const branch = doctorData?.data?.find(
                  (d) => d.branch.branchId === id
                )?.branch.branchName;
                return (
                  <Option key={id} value={id}>
                    {branch}
                  </Option>
                );
              })}
            </Select>

            <Select
              placeholder="Filter by Speciality"
              style={{ width: 200 }}
              allowClear
              onChange={(val) => setSelectedSpeciality(val)}
            >
              {[
                ...new Set(
                  doctorData?.data?.map((d) => d.speciality.specialityId)
                ),
              ].map((id) => {
                const speciality = doctorData?.data?.find(
                  (d) => d.speciality.specialityId === id
                )?.speciality.specialityName;
                return (
                  <Option key={id} value={id}>
                    {speciality}
                  </Option>
                );
              })}
            </Select>

            <DatePicker
              value={selectedDate}
              onChange={(date) => setSelectedDate(date || dayjs())}
            />
          </div>
        }
      >
        {doctorLoading ? (
          <div style={{ textAlign: "center", padding: 50 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Row gutter={[16, 16]}>
            {filteredDoctors?.length > 0 ? (
              filteredDoctors.map((doc, index) => (
                <Col xs={24} sm={12} md={8} lg={6} key={index}>
                  <Card
                    hoverable
                    style={{
                      borderRadius: 12,
                      textAlign: "center",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      backgroundColor: "rgba(222, 245, 248, 1)",
                    }}
                    styles={{
                      body: {
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                      },
                    }}
                    cover={
                      <Avatar
                        size={96}
                        src={doc.profileImageUrl || null}
                        icon={!doc.profileImageUrl && <UserOutlined />}
                        style={{ margin: "20px  auto 10px auto " }}
                      />
                    }
                  >
                    <div style={{ flex: 1 }}>
                      <Title level={5} style={{ marginTop: -25 }}>
                        {doc.name}
                      </Title>
                      <Text type="secondary">
                        {doc.mergedSpecialities
                          .map((s) => s.specialityName)
                          .join(", ")}
                      </Text>
                      <div style={{ marginTop: 8 }}>
                        <Text strong>Branches:</Text>{" "}
                        {doc.mergedBranches.map((b) => b.branchName).join(", ")}
                      </div>
                      <div>
                        <Text strong>Experience:</Text> {doc.experience} years
                      </div>
                    </div>
                    <Button
                      type="primary"
                      block
                      style={{ marginTop: 16, borderRadius: 8 }}
                      onClick={() => openBooking(doc)}
                    >
                      Book Appointment
                    </Button>
                  </Card>
                </Col>
              ))
            ) : (
              <Col span={24}>
                <p style={{ textAlign: "center", marginTop: 20 }}>
                  No doctors available with selected filters.
                </p>
              </Col>
            )}
          </Row>
        )}
      </Card>

      {/* Booking Modal */}
      <Modal
        title="Book Appointment"
        open={bookModalVisible}
        onCancel={() => setBookModalVisible(false)}
        footer={null}
        width={1100} // Increased width
        styles={{
          body: { maxHeight: "80vh", overflowY: "auto", overflowX: "hidden" },
        }}
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical" onFinish={handleBook}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="doctorId" label="Doctor">
                <>
                  <Input value={selectedDoctor?.name} disabled />{" "}
                  {/* just for display */}
                  <input type="hidden" value={selectedDoctor?.doctorId} />{" "}
                  {/* real value for payload */}
                </>
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                name="branchId"
                label="Select Branch"
                rules={[{ required: true, message: "Please select a branch" }]}
              >
                <Select placeholder="Select Branch">
                  {selectedDoctor?.mergedBranches.map((b) => (
                    <Option key={b.branchId} value={b.branchId}>
                      {b.branchName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                name="specialityId"
                label="Select Speciality"
                rules={[
                  { required: true, message: "Please select a speciality" },
                ]}
              >
                <Select placeholder="Select Speciality">
                  {selectedDoctor?.mergedSpecialities.map((s) => (
                    <Option key={s.specialityId} value={s.specialityId}>
                      {s.specialityName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                name="appointmentDate"
                label="Appointment Date"
                rules={[{ required: true, message: "Please pick a date" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  value={selectedDate}
                  onChange={(date) => setSelectedDate(date || dayjs())}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="appointmentType"
                label="Appointment Type"
                initialValue="General"
                rules={[
                  { required: true, message: "Please select appointment type" },
                ]}
              >
                <Select>
                  <Option value="General">General</Option>
                  <Option value="Follow-up">Follow-up</Option>
                  <Option value="Emergency">Emergency</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                name="callType"
                label="Call Type"
                initialValue="Video"
                rules={[{ required: true, message: "Please select call type" }]}
              >
                <Select>
                  <Option value="Video">Video</Option>
                  <Option value="Audio">Audio</Option>
                  <Option value="In-Person">In-Person</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="appointTime"
            rules={[{ required: true, message: "Please select a time slot" }]}
          >
            <Input type="hidden" />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.appointTime !== curr.appointTime}
          >
            {/* <Tabs
              defaultActiveKey="morning"
              onChange={(key) => setScheduleType(key)}
            >
              {["morning", "afternoon", "evening"].map((period) => (
                <TabPane
                  tab={period.charAt(0).toUpperCase() + period.slice(1)}
                  key={period}
                >
                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, curr) =>
                      prev.appointTime !== curr.appointTime
                    }
                  >
                    {({ getFieldValue, setFieldsValue }) => (
                      <>
                        {availableSlot?.[period]?.available?.map(
                          (slot, idx) => {
                            const appointmentDate =
                              getFieldValue("appointmentDate");
                            const slotDateTime = dayjs(
                              `${appointmentDate?.format("YYYY-MM-DD")} ${
                                slot.start
                              }`,
                              "YYYY-MM-DD hh:mm A" // important to match AM/PM format
                            );

                            const isPast = slotDateTime.isBefore(dayjs());
                            const isSelected =
                              getFieldValue("appointTime") === slot.start;

                            return (
                              <Button
                                key={idx}
                                type={isSelected ? "primary" : "default"}
                                disabled={isPast}
                                style={{ margin: "5px" }}
                                onClick={() =>
                                  setFieldsValue({ appointTime: slot.start })
                                }
                              >
                                {slot.start}
                              </Button>
                            );
                          }
                        )}

                        {availableSlot?.[period]?.booked?.map((slot, idx) => (
                          <Button
                            key={`booked-${idx}`}
                            disabled
                            style={{ margin: "5px" }}
                          >
                            {slot.start}
                          </Button>
                        ))}
                      </>
                    )}
                  </Form.Item>
                </TabPane>
              ))}
            </Tabs> */}

            <Tabs
              defaultActiveKey="morning"
              onChange={(key) => setScheduleType(key)}
            >
              {["morning", "afternoon", "evening"].map((period) => (
                <TabPane
                  tab={period.charAt(0).toUpperCase() + period.slice(1)}
                  key={period}
                >
                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, curr) =>
                      prev.appointTime !== curr.appointTime
                    }
                  >
                    {({ getFieldValue, setFieldsValue }) => (
                      <>
                        {[
                          ...(availableSlot?.[period]?.available || []),
                          ...(availableSlot?.[period]?.booked || []),
                        ]
                          .sort((a, b) =>
                            dayjs(a.start, "hh:mm a").isAfter(
                              dayjs(b.start, "hh:mm a")
                            )
                              ? 1
                              : -1
                          )
                          .map((slot, idx) => {
                            const appointmentDate =
                              getFieldValue("appointmentDate");
                            const slotDateTime = dayjs(
                              `${appointmentDate?.format("YYYY-MM-DD")} ${
                                slot.start
                              }`,
                              "YYYY-MM-DD hh:mm a"
                            );

                            // const isPast = slotDateTime.isBefore(dayjs());
                            const isBooked = availableSlot?.[
                              period
                            ]?.booked?.some((b) => b.start === slot.start);
                            const isSelected =
                              getFieldValue("appointTime") === slot.start;

                            // Color logic
                            let bgColor = "#e0e0e0"; // default gray (disabled/past)
                            let textColor = "#000";
                            if (isSelected) {
                              bgColor = "#1c2bd3"; // primary blue
                              textColor = "#fff";
                            } else if (isBooked) {
                              bgColor = "#ff5f5f"; // red for booked
                              textColor = "#fff";
                            } 
                            // else if (isPast) {
                            //   bgColor = "#d3d3d3"; // light gray for past
                            // }
                             else {
                              bgColor = "#4caf50"; // green for available
                              textColor = "#fff";
                            }

                            return (
                              <Button
                                key={idx}
                                disabled={ isBooked}
                                style={{
                                  margin: "6px",
                                  backgroundColor: bgColor,
                                  color: textColor,
                                  borderRadius: "20px",
                                  minWidth: "90px",
                                  boxShadow: isSelected
                                    ? "0 3px 10px rgba(28, 43, 211, 0.4)"
                                    : "0 2px 5px rgba(0,0,0,0.15)",
                                  transition: "all 0.2s ease-in-out",
                                  fontWeight: isSelected ? "bold" : "normal",
                                }}
                                onClick={() =>
                                  // !isPast &&
                                  !isBooked &&
                                  setFieldsValue({ appointTime: slot.start })
                                }
                              >
                                {slot.start}
                              </Button>
                            );
                          })}
                      </>
                    )}
                  </Form.Item>
                </TabPane>
              ))}
            </Tabs>
          </Form.Item>

          <Col span={24}>
            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={1} placeholder="Enter notes (optional)" />
            </Form.Item>
          </Col>

          <Button
            type="primary"
            htmlType="submit"
            block
            loading={bookingLoading}
          >
            Confirm Booking
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default DoctorBooking;
